import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import employeeService from "./employeeService";

const initialState = {
  employees: [],
  employee: null,

  isCreateLoading: false,
  isCreateError: false,
  isCreateSuccess: false,
  createErrorMessage: "",

  isListLoading: false,
  isListError: false,
  isListSuccess: false,
  listErrorMessage: "",

  isDetailsLoading: false,
  isDetailsError: false,
  isDetailsSuccess: false,
  detailsErrorMessage: "",

  isUpdateLoading: false,
  isUpdateError: false,
  isUpdateSuccess: false,
  updateErrorMessage: "",

  isDeleteLoading: false,
  isDeleteError: false,
  isDeleteSuccess: false,
  deleteErrorMessage: "",
};

//Create Employee
export const createEmployee = createAsyncThunk(
  "employee/createEmployee",
  async (employee, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;

      const createdEmployee = await employeeService.createEmployee(
        employee,
        token
      );
      return createdEmployee;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//Get All Employees
export const getAllEmployees = createAsyncThunk(
  "employee/getAllEmployees",
  async (_, thunkAPI) => {
    try {
      // console.log(thunkAPI.getState().user.userInfo.token);
      const token = thunkAPI.getState().user.userInfo.token;
      const employees = await employeeService.getAllEmployees(token);

      return employees;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//Get Employee By Id
export const getEmployeeById = createAsyncThunk(
  "employee/getEmployeeById",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const employee = await employeeService.getEmployeeById(id, token);
      return employee;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//Update Employee
export const updateEmployee = createAsyncThunk(
  "employee/updateEmployee",
  async ({ id, employeeData, userData }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const updatedEmployee = await employeeService.updateEmployee(
        id,
        employeeData,
        userData,
        token
      );
      return updatedEmployee;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//Delete Employee
export const deleteEmployee = createAsyncThunk(
  "employee/deleteEmployee",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const deletedEmployee = await employeeService.deleteEmployee(id, token);
      return deletedEmployee;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update Employee Paid Status
export const updateEmployeePaidStatus = createAsyncThunk(
  "employees/updatePaidStatus",
  async (paidEmployeesIds, {getState}) => {
    const {employees} = getState().employees

    const updatedEmployees = employees.map((employee) => ({
      ...employee,
      isPaid : paidEmployeesIds.includes(employee._id)
    }))

    return updatedEmployees;
  } 
)

const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {
    resetEmployeeCreate: (state) => {
      state.isCreateLoading = false;
      state.isCreateError = false;
      state.isCreateSuccess = false;
      state.createErrorMessage = "";
    },
    resetEmployeeList: (state) => {
      state.employees = [];
      state.isListLoading = false;
      state.isListError = false;
      state.isListSuccess = false;
      state.listErrorMessage = "";
    },
    resetEmployeeDetails: (state) => {
      state.employee = null;
      state.isDetailsLoading = false;
      state.isDetailsError = false;
      state.isDetailsSuccess = false;
      state.detailsErrorMessage = "";
    },
    resetEmployeeUpdate: (state) => {
      state.isUpdateLoading = false;
      state.isUpdateError = false;
      state.isUpdateSuccess = false;
      state.updateErrorMessage = "";
    },
    resetEmployeeDelete: (state) => {
      state.isDeleteLoading = false;
      state.isDeleteError = false;
      state.isDeleteSuccess = false;
      state.deleteErrorMessage = "";
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createEmployee.pending, (state) => {
        state.isCreateLoading = true;
        state.isCreateError = false;
        state.isCreateSuccess = false;
        state.createErrorMessage = "";
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.isCreateLoading = false;
        state.isCreateError = false;
        state.isCreateSuccess = true;
        state.createErrorMessage = "";

        state.employees.push(action.payload);
        state.employee = action.payload;
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.isCreateLoading = false;
        state.isCreateError = true;
        state.isCreateSuccess = false;
        state.createErrorMessage = action.payload;
      })
      .addCase(getAllEmployees.pending, (state) => {
        state.isListLoading = true;
        state.isListError = false;
        state.isListSuccess = false;
        state.listErrorMessage = "";
      })
      .addCase(getAllEmployees.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.isListError = false;
        state.isListSuccess = true;
        state.listErrorMessage = "";

        state.employees = action.payload;
      })
      .addCase(getAllEmployees.rejected, (state, action) => {
        state.isListLoading = false;
        state.isListError = true;
        state.isListSuccess = false;
        state.listErrorMessage = action.payload;
      })
      .addCase(getEmployeeById.pending, (state) => {
        state.isDetailsLoading = true;
        state.isDetailsError = false;
        state.isDetailsSuccess = false;
        state.detailsErrorMessage = "";
      })
      .addCase(getEmployeeById.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.isDetailsError = false;
        state.isDetailsSuccess = true;
        state.detailsErrorMessage = "";

        state.employee = action.payload;
      })
      .addCase(getEmployeeById.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.isDetailsError = true;
        state.isDetailsSuccess = false;
        state.detailsErrorMessage = action.payload;
      })
      .addCase(updateEmployee.pending, (state) => {
        state.isUpdateLoading = true;
        state.isUpdateError = false;
        state.isUpdateSuccess = false;
        state.updateErrorMessage = "";
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.isUpdateLoading = false;
        state.isUpdateError = false;
        state.isUpdateSuccess = true;
        state.updateErrorMessage = "";

        state.employees = state.employees.map((employee) =>
          employee._id === action.payload._id ? action.payload : employee
        );

        state.employee = action.payload;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.isUpdateLoading = false;
        state.isUpdateError = true;
        state.isUpdateSuccess = false;
        state.updateErrorMessage = action.payload;
      })
      .addCase(deleteEmployee.pending, (state) => {
        state.isDeleteLoading = true;
        state.isDeleteError = false;
        state.isDeleteSuccess = false;
        state.deleteErrorMessage = "";
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.isDeleteLoading = false;
        state.isDeleteError = false;
        state.isDeleteSuccess = true;
        state.deleteErrorMessage = "";

        state.employees = state.employees.filter(
          (employee) => employee._id !== action.payload
        );
        state.employee = null;
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.isDeleteLoading = false;
        state.isDeleteError = true;
        state.isDeleteSuccess = false;
        state.deleteErrorMessage = action.payload;
      })
      .addCase(updateEmployeePaidStatus.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
  },
});

export const {
  resetEmployeeCreate,
  resetEmployeeList,
  resetEmployeeDetails,
  resetEmployeeUpdate,
  resetEmployeeDelete,
} = employeeSlice.actions;

export default employeeSlice.reducer;
